import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Warm the cache with the main image of other variants so switching to a different
    // mockup doesn't download it from scratch (the remaining white flash). Fires once the
    // browser is idle, and again the moment the shopper hovers the variant picker.
    const preloadVariantImages = () => this.#preloadVariantImages();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadVariantImages, { timeout: 2500 });
    } else {
      setTimeout(preloadVariantImages, 1500);
    }
    target?.querySelector('variant-picker')?.addEventListener('pointerenter', preloadVariantImages, {
      signal,
      once: true,
    });
  }

  #controller = new AbortController();

  #didPreloadVariantImages = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by morphing the current media gallery into the new one.
   *
   * We morph in place instead of replacing the element wholesale so that unchanged media
   * (keyed by `data-media-id`) keep their exact `<img>` DOM node — and therefore their
   * already-decoded bitmap — across the variant swap. `morph`'s attribute copier skips
   * `src`/`srcset` when the value is unchanged, so a size change that reuses the same image
   * causes zero teardown and zero flash. When the image genuinely changes, the incoming
   * first image is preloaded and decoded first (see `#decodeFirstImage`) so the swap lands
   * with the bitmap already in cache.
   *
   * The `<slideshow-component>` instance is preserved by the morph, so after the DOM
   * settles we re-sync it to the first slide (the variant's featured media is rendered
   * first by the server).
   *
   * When the visible image genuinely changes, we also mask the swap: the incoming image is
   * inserted at `opacity: 0` and fades in over ~150ms once its bitmap is decoded, while the
   * container's neutral scheme background shows through instead of white. A size-only
   * variant reuses the same media, so it gets no class, no transition — an instant swap.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Does the visible (first) image actually change with this variant?
    const currentFirstImage = this.querySelector('.product-media__image');
    const incomingFirstImage = newMediaGallery.querySelector('.product-media__image');
    const imageChanged =
      incomingFirstImage != null &&
      (currentFirstImage == null ||
        currentFirstImage.getAttribute('src') !== incomingFirstImage.getAttribute('src') ||
        currentFirstImage.getAttribute('srcset') !== incomingFirstImage.getAttribute('srcset'));

    // Start the incoming image hidden so it can fade in once decoded (see #decodeFirstImage).
    if (imageChanged) {
      incomingFirstImage.classList.add('product-media__image--variant-entering');
    }

    await this.#decodeFirstImage(newMediaGallery);

    // A newer variant update may have already disconnected this gallery while we decoded.
    if (!this.isConnected) return;

    // `in-viewport` is a runtime-only attribute set by the slideshow's IntersectionObserver
    // (never rendered server-side) that gates native drag/swipe scrolling via CSS
    // (`slideshow-component:not([in-viewport]) slideshow-slides { overflow: hidden; }`).
    // morph's generic attribute diff strips it because the freshly-fetched HTML never has
    // it, and since morph preserves the slideshow instance instead of reconnecting it, the
    // observer never re-adds it — leaving swipe/drag dead while JS-driven nav (dots/arrows)
    // keeps working via scrollTo(). Capture it before the morph and restore it after.
    const wasInViewport = this.querySelector('slideshow-component')?.hasAttribute('in-viewport') ?? false;

    morph(this, newMediaGallery, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => (node instanceof HTMLElement && node.dataset.mediaId) || undefined,
    });

    if (wasInViewport) {
      this.refs.slideshow?.setAttribute('in-viewport', '');
    }

    if (imageChanged) {
      // The morphed-in image is now in the DOM at opacity 0 with its bitmap already decoded.
      // Drop the class after a paint so CSS transitions opacity 0 -> 1 (~150ms).
      const enteringImage = this.querySelector('.product-media__image--variant-entering');
      if (enteringImage) {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => enteringImage.classList.remove('product-media__image--variant-entering'))
        );
      }
    }

    // Keep the slideshow's internal state (scroll position, active index) in sync with the
    // freshly morphed slides. The variant's featured media is always the first slide.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.refs.slideshow?.select?.(0, undefined, { animate: false });
    });
  };

  /**
   * Preloads and decodes the first image of the incoming media gallery.
   * Resolves when the image is ready to paint, on load error, or after a short timeout,
   * so a slow or failing image never blocks the variant swap.
   *
   * @param {Element} gallery - The incoming media gallery element.
   * @returns {Promise<void>}
   */
  #decodeFirstImage(gallery) {
    const img = gallery.querySelector('.product-media__image') || gallery.querySelector('img');
    if (!img) return Promise.resolve();

    const preloader = new Image();
    if (img.sizes) preloader.sizes = img.sizes;
    const srcset = img.getAttribute('srcset');
    if (srcset) preloader.srcset = srcset;
    preloader.src = img.getAttribute('src') || '';

    const ready =
      typeof preloader.decode === 'function'
        ? preloader.decode().catch(() => {})
        : new Promise((resolve) => {
            preloader.onload = preloader.onerror = () => resolve();
          });

    const timeout = new Promise((resolve) => setTimeout(resolve, 500));

    return Promise.race([ready, timeout]);
  }

  /** How many variant images to prepare immediately; the rest trickle in on idle. */
  static #EAGER_PRELOAD_COUNT = 4;

  /** Keeps in-flight preloader Images alive until they finish (so GC can't drop the request). */
  #preloadedImages = [];

  /**
   * Warms the browser cache with the featured image of *every* distinct variant of this
   * product, so switching to any other mockup resolves from cache instead of a fresh
   * download (which is the last remaining white flash).
   *
   * The candidate list is a JSON array of `srcset` strings emitted by
   * `product-media-gallery-content.liquid`, one per distinct variant media (deduplicated by
   * media id server-side, no artificial count cap beyond a large safety limit).
   *
   * Staged so it never competes with the initial page load:
   *   - first `#EAGER_PRELOAD_COUNT` images are prepared right away;
   *   - the remainder trickle in one per idle tick.
   * Each preloader carries the same `sizes` as the live gallery image and `fetchpriority=low`,
   * so it downloads the exact candidate the real <img> will use, at low priority.
   *
   * Idempotent — safe to call from both the idle and hover triggers.
   */
  #preloadVariantImages() {
    if (this.#didPreloadVariantImages || !this.isConnected) return;

    const dataEl = this.querySelector('script[data-variant-image-preload]');
    if (!dataEl?.textContent) return;

    let srcsets;
    try {
      srcsets = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(srcsets) || srcsets.length === 0) return;

    this.#didPreloadVariantImages = true;

    const sizes = this.querySelector('.product-media__image')?.getAttribute('sizes') || undefined;

    // De-duplicate identical srcset strings so nothing is fetched twice.
    const seen = new Set();
    /** @type {string[]} */
    const queue = [];
    for (const srcset of srcsets) {
      if (typeof srcset === 'string' && srcset && !seen.has(srcset)) {
        seen.add(srcset);
        queue.push(srcset);
      }
    }

    // Stage 1: prepare the images most likely to be picked next straight away.
    for (const srcset of queue.slice(0, MediaGallery.#EAGER_PRELOAD_COUNT)) {
      this.#preloadOneImage(srcset, sizes);
    }

    // Stage 2: trickle the rest in, one per idle tick, so a 12-variant canvas is fully
    // covered without ever blocking the initial render.
    const rest = queue.slice(MediaGallery.#EAGER_PRELOAD_COUNT);
    if (rest.length === 0) return;

    const schedule =
      'requestIdleCallback' in window
        ? (/** @type {() => void} */ fn) => requestIdleCallback(fn, { timeout: 4000 })
        : (/** @type {() => void} */ fn) => setTimeout(fn, 300);

    let index = 0;
    const pump = () => {
      if (!this.isConnected || index >= rest.length) return;
      this.#preloadOneImage(rest[index++], sizes);
      schedule(pump);
    };
    schedule(pump);
  }

  /**
   * Downloads and decodes a single variant image off-screen at low priority.
   * @param {string} srcset - The srcset string for this variant's featured image.
   * @param {string | undefined} sizes - The `sizes` attribute of the live gallery image.
   */
  #preloadOneImage(srcset, sizes) {
    const candidates = srcset
      .split(',')
      .map((candidate) => candidate.trim().split(/\s+/)[0])
      .filter(Boolean);
    if (candidates.length === 0) return;

    const img = new Image();
    img.setAttribute('fetchpriority', 'low');
    if (sizes) img.sizes = sizes;
    img.srcset = srcset;
    img.src = candidates[candidates.length - 1];
    img.decode?.().catch(() => {});

    this.#preloadedImages.push(img);
    const release = () => {
      const at = this.#preloadedImages.indexOf(img);
      if (at !== -1) this.#preloadedImages.splice(at, 1);
    };
    img.addEventListener('load', release, { once: true });
    img.addEventListener('error', release, { once: true });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
